import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Menu, Globe, X, AlignJustify } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSettings, useMediaLibrary } from '@/hooks/useSupabaseData';
import { useScreenSize } from '@/hooks/use-mobile';
import { useOptimizedNavigationMenu } from '@/hooks/useOptimizedNavigationMenu';
import ResponsiveNavigationSystem from './ResponsiveNavigationSystem';
import EnhancedMobileSidebar from './EnhancedMobileSidebar';
import { featureFlags } from '@/utils/featureFlags';
import { useHeaderSize } from '@/hooks/useHeaderSize';
import UnifiedLogo from '@/components/logo/UnifiedLogo';

interface UnifiedHeaderProps {
  isTransparent?: boolean;
  currentSection?: number;
  variant?: 'home' | 'internal' | 'sticky';
  isVisible?: boolean;
  sectionThemes?: Record<number, 'light' | 'dark'>;
}

// Define section themes for homepage
const sectionThemes = {
  0: 'dark', // Hero - dark background (video)
  1: 'light', // About - light background  
  2: 'dark', // Sectors - dark background
  3: 'light', // Vision - light background
  4: 'dark', // Contact - dark background
};

const UnifiedHeader = ({ 
  isTransparent = false, 
  currentSection = 0, 
  variant = 'internal',
  isVisible = true,
  sectionThemes = {}
}: UnifiedHeaderProps) => {
  // Ensure CSS var --header-h is measured as early as possible
  useHeaderSize();
  const { language, setLanguage } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const { isMobileOrTablet } = useScreenSize();
  const location = useLocation();

  // Fetch navigation from database
  const { data: navigationMenu } = useOptimizedNavigationMenu('main-header');

  // Transform navigation items
  const allNavigationItems = React.useMemo(() => {
    if (!navigationMenu?.items) return [];
    
    return navigationMenu.items.map(item => ({
      label: language === 'ar' ? item.title_ar : item.title_en,
      href: item.external_url || (item.slug ? `/${String(item.slug).replace(/^\/+/, '')}` : '#')
    }));
  }, [navigationMenu?.items, language]);

  const isActivePath = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver for dynamic theme changes (للصفحات sections)
  useEffect(() => {
    if (variant !== 'home' || !featureFlags.ff_header_auto_theme) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionIndex = parseInt(entry.target.getAttribute('data-section-index') || '0');
            const theme = entry.target.getAttribute('data-theme') as 'light' | 'dark' || 'light';
            
            console.log(`🎭 UnifiedHeader IntersectionObserver: القسم ${sectionIndex} نشط`, { 
              theme, 
              currentSection: sectionIndex 
            });
            
            // تحديث القسم النشط إذا كان مختلفاً
            if (sectionIndex !== currentSection) {
              // يمكن إضافة callback هنا لتحديث currentSection في المكون الأب
            }
          }
        });
      },
      {
        threshold: 0.5, // يُعتبر القسم نشطاً عند ظهور 50% منه
        rootMargin: '-20% 0px -20% 0px' // هامش للتأكد من دقة الاكتشاف
      }
    );

    // مراقبة جميع الأقسام
  const sections = document.querySelectorAll('.pg-section[data-theme]');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
      observer.disconnect();
    };
  }, [variant, currentSection]);

  // Determine current theme for homepage (use dynamic data-theme when available)
  let currentTheme: 'light' | 'dark' = 'light';
  if (variant === 'home' && featureFlags.ff_header_auto_theme) {
    // Try to read the theme from the current section DOM if rendered
    const el = document.getElementById(`section-${currentSection}`)?.querySelector('.pg-section') as HTMLElement | null;
    const domTheme = (el?.getAttribute?.('data-theme') as 'light' | 'dark' | null) || null;
    currentTheme = domTheme || sectionThemes[currentSection] || 'light';
  }
  const isDarkSection = currentTheme === 'dark' && variant === 'home' && featureFlags.ff_header_auto_theme;
  
  console.log(`🎭 UnifiedHeader: ثيمة القسم الحالي`, { 
    currentSection, 
    currentTheme, 
    isDarkSection, 
    sectionThemes,
    variant,
    autoThemeEnabled: featureFlags.ff_header_auto_theme
  });
  
  // Determine logo variant based on context
  const getLogoVariant = (): 'light' | 'dark' => {
    if (variant === 'home' && !isScrolled && isDarkSection) {
      return 'light'; // White logo on dark background
    }
    return 'dark'; // Dark logo on light background
  };

  // Dynamic styling functions
  const getHeaderClasses = () => {
    const baseClasses = "transition-all duration-500 ease-in-out";
    // Homepage: keep header fully transparent when not scrolled to avoid any glassy backdrop behind the logo
    if (variant === 'home' && !isScrolled) {
      return `fixed top-0 left-0 right-0 z-50 bg-transparent backdrop-blur-0 shadow-none ${baseClasses}`;
    }
    
    if (variant === 'sticky') {
      return `fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 ${baseClasses}`;
    }
    
    if (variant === 'home') {
      return `fixed top-0 left-0 right-0 z-50 ${baseClasses} ${
        isScrolled || !isTransparent
          ? 'bg-white/95 backdrop-blur-md shadow-elegant'
          : isDarkSection
            ? 'bg-transparent'
            : 'bg-transparent'
      }`;
    }
    
    // Internal pages - always show with white background
    return `fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 ${baseClasses}`;
  };

  const getTextColorClass = () => {
    if (variant === 'home') {
      return isScrolled 
        ? 'text-primary' 
        : isTransparent && isDarkSection 
          ? 'text-white' 
          : 'text-primary';
    }
    return 'text-primary';
  };

  const getIconColorClass = () => {
    if (variant === 'home') {
      return isScrolled 
        ? 'text-primary' 
        : isTransparent && isDarkSection 
          ? 'text-white' 
          : 'text-primary';
    }
    return 'text-primary';
  };

  const getStickyProps = () => {
    if (variant === 'sticky') {
      return {
        initial: { y: -100, opacity: 0 },
        animate: { 
          y: isVisible ? 0 : -100,
          opacity: isVisible ? 1 : 0
        }
      };
    }
    
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 }
    };
  };

  return (
    <>
      <motion.header
        className={getHeaderClasses()}
        initial={getStickyProps().initial}
        animate={getStickyProps().animate}
        transition={{ duration: 0.3 }}
        data-site-header
      >
        <div className="container mx-auto px-4 header-home">
          <div className="flex items-center justify-between h-[70px] md:h-[75px] lg:h-[85px] py-2 md:py-3 lg:py-4">
            
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-shrink-0 bg-transparent"
            >
              <UnifiedLogo
                variant={getLogoVariant()}
                position="header"
                className="transition-all duration-300"
              />
            </motion.div>

            {/* Desktop Navigation System */}
            <ResponsiveNavigationSystem
              variant={variant}
              isScrolled={isScrolled}
              isDarkSection={isDarkSection}
              className={`flex-1 justify-center mx-8 min-w-0 ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}
            />

            {/* Right Controls */}
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              
              {/* Search Icon */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`p-2 rounded-full transition-colors duration-300 ${getIconColorClass()}`}
              >
                <Search size={20} />
              </motion.button>

              {/* Language Toggle */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className={`flex items-center space-x-1 rtl:space-x-reverse px-3 py-2 rounded-full border transition-all duration-300 ${
                  variant === 'home' && !isScrolled && isDarkSection
                    ? 'border-white/30 text-white hover:bg-white/10 hover:border-white/50 backdrop-blur-sm'
                    : variant === 'internal'
                    ? 'border-white/30 text-white hover:bg-white/10 hover:border-white/50 backdrop-blur-sm'
                    : 'border-primary text-primary hover:bg-primary hover:text-white'
                } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}
              >
                <Globe size={16} />
                <span className="text-sm font-medium">{language === 'ar' ? 'EN' : 'ع'}</span>
              </motion.button>

              {/* Mobile Menu Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className={`lg:hidden p-2 rounded-full transition-colors duration-300 ${getIconColorClass()}`}
              >
                <Menu size={20} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Enhanced Mobile Sidebar */}
      <EnhancedMobileSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        variant={variant}
      />
    </>
  );
};

export default UnifiedHeader;